import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { commentApi, userApi } from '../api/endpoints.js';
import { useApi } from '../hooks/useApi.js';
import { useToast } from '../hooks/useToast.jsx';
import { useApiError } from '../hooks/useApiError.js';
import Card from './Card.jsx';
import Badge from './Badge.jsx';
import EmptyState from './EmptyState.jsx';
import Spinner from './Spinner.jsx';
import { TextArea } from './Field.jsx';
import MentionInput, { mentionOptions, mentionsIn } from './MentionInput.jsx';
import { AttachmentList, AttachmentPicker } from './Attachments.jsx';
import { RichText, RichTextToolbar } from './RichText.jsx';
import { formatDateTime, fullName } from '../utils/format.js';

/**
 * The conversation on a customer.
 *
 * Separate from the activity feed underneath it: that one is the system's
 * append-only record of what happened, this one is what people said. A telepro
 * and the sales rep who took the account over talk to each other here.
 *
 * A pinned comment floats to the top - it is the note nobody should miss before
 * picking up the phone.
 *
 * The SAME thread is shown on the customer page and on a qualification. Splitting
 * it per lead would fragment the conversation and let a note go unseen; instead a
 * comment written from a qualification records its `leadId` and is tagged, so
 * nothing is lost and the context is still there.
 */
const CommentThread = ({ customerId = null, taskId = null, leadId = null, title, highlightId = null }) => {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const toMessage = useApiError();
  const locale = i18n.resolvedLanguage;

  const [draft, setDraft] = useState('');
  // Uploaded as they are chosen, so posting only has ids to send.
  const [files, setFiles] = useState([]);
  // Shared with the toolbar, which formats the selection in place.
  const draftRef = useRef(null);
  const [editing, setEditing] = useState(null); // { id, body }
  const [busy, setBusy] = useState(false);

  const target = taskId ? { taskId } : { customerId };
  const { data, loading, refresh } = useApi(() => commentApi.list(target), [customerId, taskId]);
  const { data: directory } = useApi(() => userApi.directory(), []);

  // Who can be called out. Everyone already in the thread is notified anyway -
  // a mention is for pulling in somebody who is not.
  const options = useMemo(
    () => mentionOptions((directory?.items ?? []).filter((person) => person.role !== 'IT'), t),
    [directory, t],
  );

  const post = async () => {
    if (!draft.trim()) return;
    setBusy(true);
    try {
      // Derived from the text: an "@Sarah" you deleted notifies nobody.
      await commentApi.create({
        ...target,
        leadId,
        body: draft,
        mentions: mentionsIn(draft, options),
        attachmentIds: files.map((file) => file.id),
      });
      setFiles([]);
      setDraft('');
      refresh();
    } catch (err) {
      toast.error(toMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async () => {
    try {
      await commentApi.update(editing.id, { body: editing.body });
      setEditing(null);
      refresh();
    } catch (err) {
      toast.error(toMessage(err));
    }
  };

  const togglePin = async (comment) => {
    try {
      await commentApi.update(comment.id, { pinned: !comment.pinned });
      refresh();
    } catch (err) {
      toast.error(toMessage(err));
    }
  };

  const remove = async (comment) => {
    try {
      await commentApi.remove(comment.id);
      refresh();
    } catch (err) {
      toast.error(toMessage(err));
    }
  };

  const rows = data?.items ?? [];

  // Arriving from a notification: bring that comment into view and flash it, so
  // you land on the sentence you were told about rather than on a long thread.
  const highlighted = useRef(null);
  useEffect(() => {
    if (!highlightId || !rows.length) return;
    highlighted.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [highlightId, rows.length]);

  return (
    <Card title={`${title ?? t('comments.title')}${rows.length ? ` (${rows.length})` : ''}`}>
      {/* Composer first: adding a note is the common action, not reading old ones. */}
      <div className="comment-composer">
        <RichTextToolbar inputRef={draftRef} value={draft} onChange={setDraft} disabled={busy} />
        <MentionInput
          value={draft}
          onChange={setDraft}
          options={options}
          placeholder={t('comments.placeholder')}
          inputRef={draftRef}
        />
        <div className="comment-composer__actions">
          <AttachmentPicker value={files} onChange={setFiles} disabled={busy} />
          <button
            type="button"
            className="btn btn--primary btn--small"
            onClick={post}
            disabled={busy || (!draft.trim() && !files.length)}
          >
            {busy ? t('common.saving') : t('comments.post')}
          </button>
        </div>
      </div>

      {loading ? <Spinner /> : null}

      {!loading && !rows.length ? <EmptyState title={t('comments.empty')} /> : null}

      {!loading && rows.length ? (
        <ul className="comment-list">
          {rows.map((comment) => (
            <li
              key={comment.id}
              ref={comment.id === highlightId ? highlighted : null}
              className={[
                'comment',
                comment.pinned ? 'comment--pinned' : '',
                comment.id === highlightId ? 'comment--highlight' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <div className="comment__head">
                <span className="comment__author">{fullName(comment.author)}</span>
                {comment.author ? (
                  <Badge value={comment.author.role} label={t(`roles.${comment.author.role}`)} />
                ) : null}
                {comment.pinned ? <Badge value="WARM" label={t('comments.pinned')} /> : null}
                {comment.leadId ? <Badge value="SUBMITTED" label={t('comments.fromQualification')} /> : null}
                {comment.mentions?.includes('ROLE:IT') ? (
                  <Badge value="HOT" label={`@${t('comments.mentionIT')}`} />
                ) : null}
                <span className="comment__date">
                  {formatDateTime(comment.createdAt, locale)}
                  {comment.edited ? ` · ${t('comments.edited')}` : ''}
                </span>
              </div>

              {editing?.id === comment.id ? (
                <>
                  <TextArea value={editing.body} onChange={(v) => setEditing({ ...editing, body: v })} rows={3} />
                  <div className="comment__actions">
                    <button type="button" className="btn btn--small" onClick={() => setEditing(null)}>
                      {t('common.cancel')}
                    </button>
                    <button type="button" className="btn btn--small btn--primary" onClick={saveEdit}>
                      {t('common.save')}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <RichText text={comment.body} className="comment__body rich" />
                  <AttachmentList files={comment.attachments} />
                  <div className="comment__actions">
                    <button type="button" className="btn btn--ghost btn--small" onClick={() => togglePin(comment)}>
                      {comment.pinned ? t('comments.unpin') : t('comments.pin')}
                    </button>
                    {comment.canEdit ? (
                      <>
                        <button
                          type="button"
                          className="btn btn--ghost btn--small"
                          onClick={() => setEditing({ id: comment.id, body: comment.body })}
                        >
                          {t('common.edit')}
                        </button>
                        <button type="button" className="btn btn--ghost btn--small" onClick={() => remove(comment)}>
                          {t('common.delete')}
                        </button>
                      </>
                    ) : null}
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      ) : null}
    </Card>
  );
};

export default CommentThread;
